import React, { useState, useEffect, useRef } from 'react';
import { adminAPI } from '../services/api';

interface Banner {
  _id: string;
  title: string;
  subtitle: string;
  backgroundColor: string;
  gradientColor: string;
  textColor: string;
  titleFontSize: number;
  subtitleFontSize: number;
  fontFamily: string;
  icon: string;
  imageUrl: string;
  order: number;
  isActive: boolean;
}

interface BannerManagementProps {
  onRefresh: () => void;
}

const fontFamilies = [
  'Arial',
  'Helvetica',
  'Georgia',
  'Times New Roman',
  'Verdana',
  'Tahoma',
  'Trebuchet MS',
  'Impact',
  'Comic Sans MS',
  'Courier New'
];

const emojiOptions = ['🎉', '🛍️', '🔥', '⭐', '💰', '🚀', '🎁', '💝', '🌟', '✨', '🛒', '📦', '🏪', '🍔', '🍕', '☕', '🥗', '🎊', '💯', '🏆'];

const BannerManagement: React.FC<BannerManagementProps> = ({ onRefresh }) => {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingBanner, setEditingBanner] = useState<Banner | null>(null);
  const [showForm, setShowForm] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const defaultBanner: Partial<Banner> = {
    title: 'New Banner',
    subtitle: 'Add your subtitle here',
    backgroundColor: '#8b5cf6',
    gradientColor: '#6366f1',
    textColor: '#ffffff',
    titleFontSize: 17,
    subtitleFontSize: 14,
    fontFamily: 'Arial',
    icon: '🎉',
    imageUrl: '',
    order: 0,
    isActive: true
  };

  const [formData, setFormData] = useState<Partial<Banner>>(defaultBanner);

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllBanners();
      setBanners(response.data);
    } catch (error) {
      console.error('Error loading banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value
    }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          imageUrl: reader.result as string
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingBanner) {
        await adminAPI.updateBanner(editingBanner._id, formData);
      } else {
        await adminAPI.createBanner(formData);
      }
      loadBanners();
      setShowForm(false);
      setEditingBanner(null);
      setFormData(defaultBanner);
      onRefresh();
    } catch (error) {
      console.error('Error saving banner:', error);
      alert('Failed to save banner');
    }
  };

  const handleEdit = (banner: Banner) => {
    setEditingBanner(banner);
    setFormData(banner);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this banner?')) {
      try {
        await adminAPI.deleteBanner(id);
        loadBanners();
        onRefresh();
      } catch (error) {
        console.error('Error deleting banner:', error);
        alert('Failed to delete banner');
      }
    }
  };

  const handleToggleActive = async (banner: Banner) => {
    try {
      await adminAPI.updateBanner(banner._id, { isActive: !banner.isActive });
      loadBanners();
    } catch (error) {
      console.error('Error toggling banner:', error);
    }
  };

  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Loading banners...</div>;
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, color: '#1e293b' }}>🎨 Banner/Slides Management</h2>
        <button
          onClick={() => {
            setEditingBanner(null);
            setFormData(defaultBanner);
            setShowForm(true);
          }}
          style={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '10px',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '14px'
          }}
        >
          ➕ Add New Banner
        </button>
      </div>

      {/* Banner Form Modal */}
      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '30px',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ margin: '0 0 20px 0' }}>
              {editingBanner ? '✏️ Edit Banner' : '➕ Create New Banner'}
            </h3>
            
            <form onSubmit={handleSubmit}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                {/* Left Column - Form Fields */}
                <div>
                  {/* Title */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Title *</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid #ddd',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  {/* Subtitle */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Subtitle</label>
                    <input
                      type="text"
                      name="subtitle"
                      value={formData.subtitle}
                      onChange={handleInputChange}
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        border: '1px solid #ddd',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  {/* Icon Selection */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Icon/Emoji</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {emojiOptions.map(emoji => (
                        <button
                          key={emoji}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, icon: emoji }))}
                          style={{
                            padding: '8px 12px',
                            fontSize: '20px',
                            border: formData.icon === emoji ? '3px solid #6366f1' : '1px solid #ddd',
                            borderRadius: '8px',
                            background: formData.icon === emoji ? '#eef2ff' : 'white',
                            cursor: 'pointer'
                          }}
                        >
                          {emoji}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Colors */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '12px' }}>Background Color</label>
                      <input
                        type="color"
                        name="backgroundColor"
                        value={formData.backgroundColor}
                        onChange={handleInputChange}
                        style={{ width: '100%', height: '40px', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '12px' }}>Gradient Color</label>
                      <input
                        type="color"
                        name="gradientColor"
                        value={formData.gradientColor}
                        onChange={handleInputChange}
                        style={{ width: '100%', height: '40px', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '12px' }}>Text Color</label>
                      <input
                        type="color"
                        name="textColor"
                        value={formData.textColor}
                        onChange={handleInputChange}
                        style={{ width: '100%', height: '40px', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                      />
                    </div>
                  </div>

                  {/* Font Settings */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '12px' }}>Font Family</label>
                      <select
                        name="fontFamily"
                        value={formData.fontFamily}
                        onChange={handleInputChange}
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}
                      >
                        {fontFamilies.map(font => (
                          <option key={font} value={font} style={{ fontFamily: font }}>{font}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '12px' }}>Title Size</label>
                      <input
                        type="number"
                        name="titleFontSize"
                        value={formData.titleFontSize}
                        onChange={handleInputChange}
                        min={10}
                        max={40}
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}
                      />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600', fontSize: '12px' }}>Subtitle Size</label>
                      <input
                        type="number"
                        name="subtitleFontSize"
                        value={formData.subtitleFontSize}
                        onChange={handleInputChange}
                        min={8}
                        max={30}
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}
                      />
                    </div>
                  </div>

                  {/* Image Upload */}
                  <div style={{ marginBottom: '15px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Banner Image (Optional)</label>
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      onChange={handleImageUpload}
                      style={{ display: 'none' }}
                    />
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        style={{
                          padding: '10px 20px',
                          background: '#f1f5f9',
                          border: '2px dashed #94a3b8',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          flex: 1
                        }}
                      >
                        📷 Upload Image
                      </button>
                      {formData.imageUrl && (
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, imageUrl: '' }))}
                          style={{
                            padding: '10px 15px',
                            background: '#fee2e2',
                            color: '#dc2626',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer'
                          }}
                        >
                          ✕ Remove
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Order & Active */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: '600' }}>Display Order</label>
                      <input
                        type="number"
                        name="order"
                        value={formData.order}
                        onChange={handleInputChange}
                        min={0}
                        style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}
                      />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', paddingTop: '25px' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          name="isActive"
                          checked={formData.isActive}
                          onChange={handleCheckboxChange}
                          style={{ width: '20px', height: '20px' }}
                        />
                        <span style={{ fontWeight: '600' }}>Active</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Right Column - Preview */}
                <div>
                  <label style={{ display: 'block', marginBottom: '10px', fontWeight: '600' }}>🔍 Live Preview</label>
                  <div
                    style={{
                      background: `linear-gradient(135deg, ${formData.backgroundColor} 0%, ${formData.gradientColor} 100%)`,
                      borderRadius: '20px',
                      padding: '32px 24px',
                      height: '200px',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                  >
                    {formData.imageUrl && (
                      <img
                        src={formData.imageUrl}
                        alt="Banner"
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          opacity: 0.3
                        }}
                      />
                    )}
                    <div style={{ fontSize: '40px', marginBottom: '16px', zIndex: 1 }}>{formData.icon}</div>
                    <h2 style={{
                      color: formData.textColor,
                      fontSize: `${formData.titleFontSize}px`,
                      fontFamily: formData.fontFamily,
                      margin: 0,
                      textAlign: 'center',
                      zIndex: 1
                    }}>
                      {formData.title}
                    </h2>
                    {formData.subtitle && (
                      <p style={{
                        color: formData.textColor,
                        fontSize: `${formData.subtitleFontSize}px`,
                        fontFamily: formData.fontFamily,
                        margin: '8px 0 0 0',
                        opacity: 0.9,
                        textAlign: 'center',
                        zIndex: 1
                      }}>
                        {formData.subtitle}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Form Buttons */}
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingBanner(null);
                    setFormData(defaultBanner);
                  }}
                  style={{
                    padding: '12px 24px',
                    background: '#f1f5f9',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '12px 24px',
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  {editingBanner ? '💾 Update Banner' : '➕ Create Banner'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Banners List */}
      {banners.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          background: '#f8fafc',
          borderRadius: '16px'
        }}>
          <div style={{ fontSize: '60px', marginBottom: '20px' }}>🎨</div>
          <h3 style={{ margin: '0 0 10px 0', color: '#64748b' }}>No Banners Yet</h3>
          <p style={{ color: '#94a3b8' }}>Create your first banner to display on the customer app</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '20px' }}>
          {banners.map((banner, index) => (
            <div
              key={banner._id}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 200px',
                gap: '20px',
                background: 'white',
                borderRadius: '16px',
                padding: '20px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                border: banner.isActive ? '2px solid #10b981' : '2px solid #e2e8f0',
                opacity: banner.isActive ? 1 : 0.7
              }}
            >
              {/* Banner Preview */}
              <div
                style={{
                  background: `linear-gradient(135deg, ${banner.backgroundColor} 0%, ${banner.gradientColor} 100%)`,
                  borderRadius: '12px',
                  padding: '24px 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  minHeight: '120px'
                }}
              >
                {banner.imageUrl && (
                  <img
                    src={banner.imageUrl}
                    alt="Banner"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      opacity: 0.3
                    }}
                  />
                )}
                <div style={{ fontSize: '32px', marginBottom: '10px', zIndex: 1 }}>{banner.icon}</div>
                <h3 style={{
                  color: banner.textColor,
                  fontSize: `${banner.titleFontSize}px`,
                  fontFamily: banner.fontFamily,
                  margin: 0,
                  textAlign: 'center',
                  zIndex: 1
                }}>
                  {banner.title}
                </h3>
                {banner.subtitle && (
                  <p style={{
                    color: banner.textColor,
                    fontSize: `${banner.subtitleFontSize}px`,
                    fontFamily: banner.fontFamily,
                    margin: '5px 0 0 0',
                    opacity: 0.9,
                    zIndex: 1
                  }}>
                    {banner.subtitle}
                  </p>
                )}
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', justifyContent: 'center' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  marginBottom: '10px'
                }}>
                  <span style={{
                    background: banner.isActive ? '#dcfce7' : '#fee2e2',
                    color: banner.isActive ? '#16a34a' : '#dc2626',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}>
                    {banner.isActive ? '✓ Active' : '✕ Inactive'}
                  </span>
                  <span style={{ color: '#94a3b8', fontSize: '12px' }}>Order: {banner.order}</span>
                </div>
                
                <button
                  onClick={() => handleEdit(banner)}
                  style={{
                    padding: '10px',
                    background: '#eef2ff',
                    color: '#6366f1',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  ✏️ Edit
                </button>
                <button
                  onClick={() => handleToggleActive(banner)}
                  style={{
                    padding: '10px',
                    background: banner.isActive ? '#fef3c7' : '#dcfce7',
                    color: banner.isActive ? '#d97706' : '#16a34a',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  {banner.isActive ? '⏸️ Disable' : '▶️ Enable'}
                </button>
                <button
                  onClick={() => handleDelete(banner._id)}
                  style={{
                    padding: '10px',
                    background: '#fee2e2',
                    color: '#dc2626',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  🗑️ Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BannerManagement;
